/**
 * MessagingLibrary — the Messages tab of the Messaging rail. A library of
 * channel-complete messages with status sub-tabs, list/grid views, per-message
 * actions — and, since 2026-08-04 (operator), FOLDER ORGANIZATION: a reusable
 * label-based folder system (collections-api + collections-ui) with a desktop
 * tree panel, folder cards on phone/tablet, drag-and-drop filing, multi-select
 * bulk moves, folder-scoped tabs/search (one click to widen), and ask-each-time
 * folder deletion (keep vs purge). Surveys live ONLY in the Surveys tool.
 *
 * Route: `messaging` · rail tab: Messages.
 */
import React, { useEffect, useState } from "react";
import { SegmentedControl, Btn, Card, Checkbox, Icon, Input, T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { ExplainerFooter, EmptyState, LoadState, RowActions } from "../../shell/composites";
import { useViewMode, ViewToggle, CARD_GRID, CardActionBar } from "../../shell/list-composites";
import { useNav } from "../../shell/router";
import { toast } from "../../shell/toast";
import { can } from "../../shell/access";
import { ChGlyph, ChannelChip, MSG_CHANNEL, msgStatusPill, triggerReading } from "./messaging-shared";
import { useMessaging } from "./useMessaging";
import { AddToFlowModal } from "./MessageComposer";
import { useFlows } from "./useFlows";
import { messagingTabs, useMessagingCounts } from "./messaging-tabs";
import { useFolderLibrary, FolderShellFrame, FolderShellBars, FolderShellOverlays } from "../shared/library-shell";
import { useT, t as i18nT } from "../../lib/i18n";

const msgTh = { textAlign: "left" as const, padding: "10px 16px", whiteSpace: "nowrap" as const };
const msgTd = { padding: "12px 16px", verticalAlign: "middle" as const };

interface Row {
  id: string; name: string; status: string;
  channels: string[]; updated: string; sentLabel?: string;
  /** `appointment_comms` rows are platform-owned appointment messages. */
  lineage?: string; trigger?: string; offsetMinutes?: number | null; groupLabel?: string;
}

/** Trigger reading + offset live in `messaging-shared` (`triggerReading`), so
 *  the row and the composer header cannot drift — and so the offset's DIRECTION
 *  is derived from the trigger in one place (CC 2026-09-03: the wire's
 *  `offsetMinutes` is always positive; the anchor decides before/after). */

/** A system-group heading. `inline` is the in-table variant (already inside a
 *  full-width cell); otherwise it carries its own top margin for the card grid. */
function GroupBand({ label, count, note, inline = false }: { label: string; count: number; note?: string; inline?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", ...(inline ? {} : { marginTop: 16, marginBottom: 8 }) }}>
      <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: T.n2 }}>{label}</span>
      <span style={{ fontSize: 11.5, color: T.soft }}>{count}</span>
      {note ? <span style={{ fontSize: 11.5, color: T.soft, fontWeight: 400 }}>{note}</span> : null}
    </div>
  );
}

/** The trigger a platform-owned message fires on — the key, and its reading. A
 *  reminder adds its offset, because trigger alone does not identify it. */
function TriggerTag({ trigger, offsetMinutes }: { trigger: string; offsetMinutes?: number | null }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3, fontWeight: 400, flexWrap: "wrap" }}>
      <span style={{ fontSize: 12, color: T.n2 }}>{triggerReading(trigger, offsetMinutes)}</span>
      <span style={{ fontSize: 11, color: T.soft, fontFamily: T.fontMono }}>{trigger}</span>
    </div>
  );
}

export default function MessagingLibrary() {
  const t = useT();
  const nav = useNav();
  const store = useMessaging();
  useFlows(); // warm the flows cache so the rail tab count is live
  const counts = useMessagingCounts();
  const [tab, setTab] = useState("all");
  const [view, setView] = useViewMode("messaging:view");
  const [q, setQ] = useState("");
  const canWrite = can("campaigns.write");
  const msgRows: Row[] = store.all().map((m) => ({ id: m.id, name: m.name, status: m.status, channels: m.channels, updated: m.updated, sentLabel: m.sentLabel, lineage: m.lineage, trigger: m.trigger, offsetMinutes: m.offsetMinutes, groupLabel: m.group?.label }));
  /* Appointment messages are a SYSTEM group, not a user folder (CC 2026-09-03
     §3): the platform owns them, a folder move on one is dropped by the
     backend, and their identity is the trigger they fire on. So they sit
     OUTSIDE the folder shell — the tree, drag-filing, multi-select and the
     status tabs all count library rows only — and the group is hidden while a
     folder scope is active, because a folder cannot hold them. */
  const apptPool = msgRows.filter((m) => m.lineage === "appointment_comms");
  const libPool = msgRows.filter((m) => m.lineage !== "appointment_comms");
  /* Folder organization — the shared Folder Library shell (family "messages"):
     extracted 2026-08-12; this screen MOUNTS it, the logic lives in
     shared/library-shell.tsx. */
  const lib = useFolderLibrary({
    family: "messages",
    ids: libPool.map((m) => m.id),
    canWrite,
    allLabel: t("collections.allItems", "All messages"),
    searchAllLabel: t("collections.searchEverywhere", "Search all messages"),
    query: q,
    failTitle: t("messaging.library.couldnTUpdateMessage", "Couldn't update message"),
    onAfterPurge: () => store.reload(),
  });
  const poolSet = new Set(lib.pool);
  const scoped = libPool.filter((m) => lib.inScope(m.id));
  const tabCounts = { all: scoped.length, draft: scoped.filter((m) => m.status === "draft").length, live: scoped.filter((m) => m.status === "live").length };
  const matches = (m: Row) => (tab === "all" || m.status === tab)
    && (!q.trim() || m.name.toLowerCase().includes(q.toLowerCase()) || (m.trigger || "").toLowerCase().includes(q.toLowerCase()));
  const rows = libPool.filter((m) => poolSet.has(m.id) && matches(m));
  const apptRows = lib.scope === "all" ? apptPool.filter(matches) : [];
  /* The band's label is SERVER-derived (`group.label`) — the platform decides
     what the system group is called, not this screen. Our string is the
     fallback for a row that arrives without one. */
  const apptBandLabel = apptRows.find((m) => m.groupLabel)?.groupLabel ?? t("messaging.library.appointmentsGroup", "Appointments");
  const scopedFolder = lib.scopedFolder;

  const dup = async (m: Row) => { try { const c = await store.duplicate(m.id); toast({ tone: "success", title: t("messaging.library.messageDuplicated", "Message duplicated"), subtitle: c ? `"${c.name}" added as a draft.` : undefined }); } catch (e) { console.warn("duplicate message failed:", e); toast({ tone: "danger", title: t("messaging.library.couldnTDuplicateMessage", "Couldn't duplicate message"), subtitle: t("messaging.library.retryInAMoment", "Retry in a moment.") }); } };
  const del = async (m: Row) => { try { await store.remove(m.id); toast({ tone: "warn", title: t("messaging.library.messageDeleted", "Message deleted") }); } catch (e) { console.warn("delete message failed:", e); toast({ tone: "danger", title: t("messaging.library.couldnTDeleteMessage", "Couldn't delete message"), subtitle: t("messaging.library.retryInAMoment", "Retry in a moment.") }); } };
  const openMsg = (m: Row) => nav.go("messaging.compose", { id: m.id });

  const [flowMsgId, setFlowMsgId] = useState<string | null>(null);
  const flowMsg = flowMsgId ? store.get(flowMsgId) : null;

  const actions = (m: Row) => (m.lineage === "appointment_comms" ? {
    /* A platform-owned row takes content edits and nothing else: no folder, no
       flow attachment, no lifecycle, no delete. Editing one publishes a new
       design version and rebinds the appointment template (§3). */
    onView: () => nav.go("messaging.compose", { id: m.id, view: 1 }),
    viewLabel: "Preview",
    onEdit: canWrite ? () => nav.go("messaging.compose", { id: m.id }) : undefined,
    moreHeader: m.name,
    moreItems: [],
  } : {
    onView: () => nav.go("messaging.compose", { id: m.id, view: 1 }),
    viewLabel: "Preview",
    onEdit: canWrite ? () => nav.go("messaging.compose", { id: m.id }) : undefined,
    onDuplicate: canWrite ? () => dup(m) : undefined,
    moreHeader: m.name,
    moreItems: [
      ...(canWrite ? [lib.foldersMenuItem(m.id)] : []),
      ...(canWrite ? [{ icon: <Icon.list size={14} />, label: t("messaging.library.addToFlow", "Add to flow…"), onClick: () => setFlowMsgId(m.id) }] : []),
      ...(canWrite && m.status === "draft" ? [{ icon: <Icon.check size={14} />, label: t("messaging.library.makeLive", "Make live"), onClick: async () => { const full = store.get(m.id); if (!full) return; try { await store.upsert({ ...full, status: "live" }); toast({ tone: "success", title: t("messaging.library.messageIsLive", "Message is live"), subtitle: i18nT("messaging.library.mNameIsAvailableToEventsAndFlows.icu", { value0: m.name }) }); } catch (e) { console.warn("make message live failed:", e); toast({ tone: "danger", title: t("messaging.library.couldnTMakeMessageLive", "Couldn't make message live"), subtitle: t("messaging.library.retryInAMoment", "Retry in a moment.") }); } } }] : []),
      ...(canWrite && m.status === "live" ? [{ icon: <Icon.edit size={14} />, label: t("messaging.library.backToDraft", "Back to draft"), onClick: async () => { const full = store.get(m.id); if (!full) return; try { await store.upsert({ ...full, status: "draft" }); toast({ tone: "info", title: t("messaging.library.backToDraft", "Back to draft"), subtitle: i18nT("messaging.library.mNameIsEditableAgainAndNoLongerUsableInFlows.icu", { value0: m.name }) }); } catch (e) { console.warn("revert message to draft failed:", e); toast({ tone: "danger", title: t("messaging.library.couldnTUpdateMessage", "Couldn't update message"), subtitle: t("messaging.library.retryInAMoment", "Retry in a moment.") }); } } }] : []),
      ...(canWrite ? ["divider" as const, { icon: <Icon.trash size={14} />, label: t("common.actions.delete", "Delete"), danger: true, onClick: () => del(m) }] : []),
    ],
  });

  const statusTabs = [
    { id: "all", label: t("messaging.library.all", "All"), count: tabCounts.all },
    { id: "live", label: t("common.status.live", "Live"), count: tabCounts.live },
    { id: "draft", label: t("messaging.library.drafts", "Drafts"), count: tabCounts.draft },
  ];

  /* One card and one row renderer, so the system group and the library render
     identically apart from what a platform-owned row cannot have: a checkbox,
     a drag handle and a folder chip. It gains the trigger instead. */
  const msgCard = (m: Row) => {
    const appt = m.lineage === "appointment_comms";
    return (
      <Card key={m.id} {...(appt ? {} : lib.dragProps(m.id))} style={{ padding: 0, display: "flex", flexDirection: "column", cursor: "pointer", overflow: "hidden", outline: !appt && lib.sel.has(m.id) ? `2px solid ${T.p1}` : "none" }} onClick={() => openMsg(m)}>
        <div style={{ padding: "16px 18px 14px", flex: 1, display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              {/* The span keeps the card's own onClick (open message) from firing
                  when the box is ticked — Checkbox has no onClick passthrough. */}
              {canWrite && !appt ? <span onClick={(e) => e.stopPropagation()} style={{ display: "inline-flex" }}><Checkbox aria-label={t("messaging.library.selectMessage", "Select message")} checked={lib.sel.has(m.id)} onChange={(next) => lib.toggleSel(m.id, next)} /></span> : null}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14.5, fontWeight: 700, color: T.n1 }}>{m.name}</div>
                {appt && m.trigger ? <TriggerTag trigger={m.trigger} offsetMinutes={m.offsetMinutes} /> : null}
              </div>
            </div>
            {msgStatusPill(m.status)}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>{m.channels.map((id) => <ChannelChip key={id} id={id} size="sm" />)}</div>
          <div style={{ marginTop: "auto", paddingTop: 10, borderTop: `1px solid ${T.border}`, fontSize: 12, color: T.soft, display: "flex", alignItems: "center" }}>
            {m.updated}{m.sentLabel ? ` · ${m.sentLabel}` : ""}{appt ? null : lib.folderChips(m.id)}
          </div>
        </div>
        <CardActionBar {...actions(m)} />
      </Card>
    );
  };

  const msgRow = (m: Row, i: number) => {
    const appt = m.lineage === "appointment_comms";
    return (
      <tr key={m.id} {...(appt ? {} : lib.dragProps(m.id))} onClick={() => openMsg(m)} style={{ borderTop: i ? `1px solid ${T.border}` : "none", cursor: "pointer", background: !appt && lib.sel.has(m.id) ? T.p4 : "transparent" }}>
        {canWrite ? <td style={{ ...msgTd, width: 34, paddingRight: 0 }} onClick={(e) => e.stopPropagation()}>{appt ? null : <Checkbox aria-label={t("messaging.library.selectMessage", "Select message")} checked={lib.sel.has(m.id)} onChange={(next) => lib.toggleSel(m.id, next)} />}</td> : null}
        <td style={{ ...msgTd, color: T.n1, fontWeight: 600 }}>
          <div style={{ display: "flex", alignItems: "center" }}>{m.name}{appt ? null : lib.folderChips(m.id)}</div>
          {appt && m.trigger ? <TriggerTag trigger={m.trigger} offsetMinutes={m.offsetMinutes} /> : null}
        </td>
        <td style={msgTd}><div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{m.channels.map((id) => <span key={id} title={MSG_CHANNEL(id).name} style={{ display: "inline-flex" }}><ChGlyph id={id} size={15} color={MSG_CHANNEL(id).hue} /></span>)}</div></td>
        <td style={msgTd}>{msgStatusPill(m.status)}</td>
        <td style={{ ...msgTd, color: T.soft }}>{m.updated}</td>
        <td style={msgTd} onClick={(e) => e.stopPropagation()}><RowActions {...actions(m)} /></td>
      </tr>
    );
  };
  const colCount = canWrite ? 6 : 5;

  return (
    <Screen rail="messaging" tabs={messagingTabs(nav, "messages", counts)} activeTab="messages">
      <PageHeader
        title={t("messaging.library.messaging", "Messaging")}
        subtitle={t("messaging.library.createAndMaintainEventMessagingAcrossEmailSmsWhatsappTiktokInstagramAnd", "Create and maintain event messaging across email, SMS, WhatsApp, TikTok, Instagram and Facebook.")}
        actions={<>
          <ViewToggle mode={view} onChange={setView} />
          {canWrite ? <Btn icon={<Icon.plus />} onClick={() => nav.go("messaging.compose", scopedFolder ? { new: 1, folder: scopedFolder } : { new: 1 })}>{t("messaging.library.newMessage", "New message")}</Btn> : null}
        </>}
      />
      <FolderShellFrame lib={lib}>
          <div style={{ padding: "10px 0 4px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <Input full={false} leading={<Icon.search size={14} />} placeholder={t("messaging.library.findAMessage", "Find a message…")} value={q} onChange={(e) => setQ(e.target.value)} style={{ minWidth: 240 }} />
            <SegmentedControl<string>
              size="sm"
              ariaLabel="Message status"
              value={tab}
              onChange={setTab}
              style={{ marginLeft: 4 }}
              options={statusTabs.map((s) => ({ value: s.id, label: s.label, count: s.count }))}
            />
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 12, color: T.soft }}>{rows.length} {t("messaging.library.of", "of")}{" "}{lib.wide ? msgRows.length : tabCounts.all}</span>
          </div>
          <FolderShellBars lib={lib}/>

          <LoadState loading={store.loading} error={store.error} empty={rows.length === 0 && apptRows.length === 0}
            noun={t("messaging.library.nounMessages", "messages")} onRetry={() => store.reload()} testid="messaging-load"
            emptyState={<div style={{ marginTop: 14 }}>
              <EmptyState variant="card" icon={<Icon.mail size={20} />}
                title={q.trim() ? t("messaging.library.emptyFilteredTitle", "No message matches that") : lib.scope !== "all" ? t("messaging.library.emptyFolderTitle", "Nothing in this folder yet") : t("messaging.library.emptyTitle", "No messages yet")}
                body={q.trim() ? t("messaging.library.emptyFilteredBody", "Try a different search, or clear it to see everything.") : lib.scope !== "all" ? t("messaging.library.emptyFolderBody", "Drag messages here, or use a message's Folders… action.") : t("messaging.library.emptyBody", "Messages you create appear here, ready to use in flows and campaigns.")}
                action={can("campaigns.write") && !q.trim() ? <Btn icon={<Icon.plus size={14} />} onClick={() => nav.go("messaging.compose", scopedFolder ? { new: 1, folder: scopedFolder } : { new: 1 })}>{t("messaging.library.newMessage", "New message")}</Btn> : null}
                secondaryAction={q.trim() ? <Btn variant="secondary" onClick={() => setQ("")}>{t("messaging.library.clearSearch", "Clear search")}</Btn> : null} />
            </div>}>
          {view === "grid" ? (
            <>
              {apptRows.length ? (
                <>
                  <GroupBand label={apptBandLabel} count={apptRows.length} note={t("messaging.library.appointmentsGroupNote", "Sent by the platform when a booking changes. Edit the content; the trigger is fixed.")} />
                  <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: 16, marginBottom: 18 }}>{apptRows.map(msgCard)}</div>
                  {rows.length ? <GroupBand label={t("messaging.library.libraryGroup", "Library")} count={rows.length} /> : null}
                </>
              ) : null}
              <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: 16, marginTop: apptRows.length ? 0 : 14 }}>
                {rows.map(msgCard)}
              </div>
            </>
          ) : (
            <Card style={{ padding: 0, marginTop: 14 }}>
              {/* ui-raw: hand-rolled <table> — kept because Table/DataTable has no
                  per-row prop passthrough (no rowProps/getRowProps), and every row
                  here carries `lib.dragProps(id)` (draggable + onDragStart +
                  onContextMenu) for drag-and-drop folder filing and the label picker.
                  Adopting Table today would silently drop folder drag-and-drop, so
                  this waits on a `rowProps` addition to Table — reported as a gap in
                  the 2026-08-31 messaging DS-conformance changelog. The three raw
                  checkboxes inside are now jrni-ui Checkbox. */}
              <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font, fontSize: 13 }}>
                <thead>
                  <tr style={{ background: T.n6, color: T.soft, fontSize: 11, textTransform: "uppercase", fontWeight: 600 }}>
                    {canWrite ? <th style={{ ...msgTh, width: 34, paddingRight: 0 }}><Checkbox aria-label="Select all" checked={rows.length > 0 && rows.every((r) => lib.sel.has(r.id))} onChange={(next) => lib.setSel(next ? new Set(rows.map((r) => r.id)) : new Set())} /></th> : null}
                    <th style={msgTh}>{t("messaging.library.message", "Message")}</th><th style={msgTh}>{t("messaging.library.channels", "Channels")}</th><th style={msgTh}>{t("messaging.library.status", "Status")}</th><th style={msgTh}>{t("messaging.library.updated", "Updated")}</th><th style={{ ...msgTh, width: 140 }}>{t("messaging.library.actions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {apptRows.length ? (
                    <>
                      <tr><td colSpan={colCount} style={{ padding: "9px 16px", background: T.p4, borderTop: `1px solid ${T.border}` }}>
                        <GroupBand label={apptBandLabel} count={apptRows.length} note={t("messaging.library.appointmentsGroupNote", "Sent by the platform when a booking changes. Edit the content; the trigger is fixed.")} inline />
                      </td></tr>
                      {apptRows.map((m, i) => msgRow(m, i))}
                      {rows.length ? <tr><td colSpan={colCount} style={{ padding: "9px 16px", background: T.n6, borderTop: `1px solid ${T.border}` }}>
                        <GroupBand label={t("messaging.library.libraryGroup", "Library")} count={rows.length} inline />
                      </td></tr> : null}
                    </>
                  ) : null}
                  {rows.map((m, i) => msgRow(m, apptRows.length ? i + 1 : i))}
                </tbody>
              </table>
            </Card>
          )}
          </LoadState>
          <ExplainerFooter>
            <b>{t("messaging.library.oneMessageManyChannels", "One message, many channels.")}</b> {t("messaging.library.pickThePlatformsAMessageGoesOutOnAndTailorTheContent", "Pick the platforms a message goes out on and tailor the content per channel. This is an early sample — sending, channel connections and analytics come next.")}</ExplainerFooter>
      </FolderShellFrame>
      {flowMsg ? <AddToFlowModal message={flowMsg} onClose={() => setFlowMsgId(null)} /> : null}
      <FolderShellOverlays lib={lib}/>
    </Screen>
  );
}
